import { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Typography,
  Stack,
} from '@mui/material';
import ReactMarkdown from 'react-markdown';

import { useProject } from '../../hooks/useProject';
import { useLang } from '../../contexts/langContext';
import { Project, Slide } from '../../types';
import { Edit } from '@mui/icons-material';
import { magicButtonProps } from '../common/ai';
import OutlineInstructionDialog from './dialogs/OutlineInstructionDialog';
import EditOutlineWizardDialog from './dialogs/EditOutlineWizardDialog';
import { useBranding } from '../../contexts/BrandingContext';

interface OutlineManagerProps {
  project: Project;
  onUpdateProject: (project: Project) => void;
  /**
   * If true, automatically invoke outline generation when the component mounts
   * (used when the project was just created with the "auto" outline option).
   */
  autoGenerate?: boolean;
}

function OutlineManager({ project, onUpdateProject, autoGenerate = false }: OutlineManagerProps) {
  const { lang } = useLang();
  const branding = useBranding();
  const outlineFineTuning = branding?.features?.enableOutlineFineTuning;
  const { 
    project: internalProject,
    suggestedOutline, 
    handleSuggestOutline, 
    handleSuggestOutlineEdits 
  } = useProject(project._id);

  const [loading, setLoading] = useState(false);
  const [isOutlineAdviserDialogOpen, setIsOutlineAdviserDialogOpen] = useState(false);
  const [isEditWizardOpen, setIsEditWizardOpen] = useState(false);
  const [wizardMode, setWizardMode] = useState<'existing' | 'generated'>('existing');
  const [generatedOutline, setGeneratedOutline] = useState<Slide[]>([]);
  const [adoptedSlides, setAdoptedSlides] = useState<boolean[]>([]);
  const [isGenerateDialogOpen, setIsGenerateDialogOpen] = useState(false);
  
  const handleConfirmGenerate = async (instruction: string, fileIds?: string[]) => {
    setLoading(true);
    try {
      const result = await handleSuggestOutline(instruction, fileIds);
      setGeneratedOutline(result);
      setAdoptedSlides(new Array(result.length).fill(true));
      setIsOutlineAdviserDialogOpen(true);
    } catch (error) {
      console.error('Error suggesting outline:', error);
      // Handle error, e.g., show a notification to the user
    } finally {
      setLoading(false);
    }
  };

  const handleToggleSlide = (index: number) => {
    const newAdoptedSlides = [...adoptedSlides];
    newAdoptedSlides[index] = !newAdoptedSlides[index];
    setAdoptedSlides(newAdoptedSlides);
  };

  const empty = project.slides.length === 0;

  // Run auto-generation once if requested
  const hasTriggeredRef = useRef(false);
  useEffect(() => {
    if (autoGenerate && !hasTriggeredRef.current && empty && internalProject) {
      hasTriggeredRef.current = true;
      (async () => {
        setLoading(true);
        try {
          const result = await handleSuggestOutline('', undefined);
          console.log('Auto outline generation result:', result);
          setGeneratedOutline(result);
          setAdoptedSlides(new Array(result.length).fill(true));
          setIsOutlineAdviserDialogOpen(true);
        } catch (error) {
          console.error('Auto outline generation failed:', error);
        } finally {
          setLoading(false);
        }
      })();
    }
  }, [autoGenerate, empty, internalProject]);

  const handleCloseEditWizard = () => {
    setIsEditWizardOpen(false);
  };

  const handleConfirmOutline = () => {
    const sourceOutline = generatedOutline.length > 0 ? generatedOutline : suggestedOutline;
    const newSlides = sourceOutline.filter((_, index) => adoptedSlides[index]);
    onUpdateProject({ ...project, slides: newSlides });
    setIsOutlineAdviserDialogOpen(false);
  };

  const instructionPlaceholders = {
    empty: {
      'en': 'Describe your expectations for the outline. For example, you can ask the AI to include specific topics, adjust the structure, or add specific content.',
      'zh-CN': '描述您对大纲的期望。例如，您可以要求 AI 包含特定主题，调整结构，或添加特定内容。',
      'zh-HK': '描述您對大綱的期望。例如，您可以要求 AI 包含特定主題，調整結構，或添加特定內容。'
    },
    editing: {
      'en': 'Describe your expectations for the outline edits. For example, you can ask the AI to reorder the slides, add a thank-you slide, or merge certain slides.',
      'zh-CN': '描述您对大纲的修改要求。例如，您可以要求 AI 重新排序幻灯片，添加感谢页，或合并某些幻灯片。',
      'zh-HK': '描述您對大綱的修改要求。例如，您可以要求 AI 重新排序幻燈片，添加感謝頁，或合併某些幻燈片。'
    }
  };

  return (
    <>
      <Stack direction="row" spacing={1} alignItems="center" justifyContent={empty ? 'center' : 'flex-start'}>
        <Button {...magicButtonProps({loading})} size={empty ? 'large' : 'medium'} onClick={() => setIsGenerateDialogOpen(true)}>
          {lang === 'en'
            ? empty
              ? 'Generate Outline'
              : 'Regenerate Outline'
            : empty
              ? '生成大纲'
              : '重新生成大纲'}
        </Button>
        {!empty && (
          <Button 
            variant="contained" 
            size="medium" 
            onClick={() => {
              setWizardMode('existing');
              setIsEditWizardOpen(true);
            }} 
            disabled={loading} 
            startIcon={<Edit />}
          >
            {lang === 'en' ? 'Edit Outline' : '编辑大纲'}
          </Button>
        )}
      </Stack>

      {/* Generate outline dialog */}
      <OutlineInstructionDialog
        open={isGenerateDialogOpen}
        onClose={() => setIsGenerateDialogOpen(false)}
        loading={loading}
        lang={lang}
        instructionPlaceholder={empty ? instructionPlaceholders.empty[lang] : instructionPlaceholders.editing[lang]}
        projectId={project._id}
        title={lang === 'en' ? 'Generate Outline' : '生成大纲'}
        confirmLabel={lang === 'en' ? 'Generate' : '生成'}
        onConfirm={handleConfirmGenerate}
      />

      <Dialog
        open={isOutlineAdviserDialogOpen}
        onClose={() => setIsOutlineAdviserDialogOpen(false)}
        aria-labelledby="outline-dialog-title"
        aria-describedby="outline-dialog-description"
        maxWidth="md"
        fullWidth
      >
        <DialogTitle id="outline-dialog-title">{lang === 'en' ? 'Outline' : '大纲'}</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            {lang === 'en' ? 'Select the slides you want to include in your project:' : '选择您要包含在项目中的幻灯片:'}
          </Typography>
          <List>
            {(generatedOutline.length > 0 ? generatedOutline : suggestedOutline).map((slide, index) => (
              <ListItem 
                key={index} 
                dense 
                onClick={() => handleToggleSlide(index)}
                sx={{
                  cursor: 'pointer',
                  bgcolor: adoptedSlides[index] ? 'background.default' : 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  mb: 1
                }}
              >
                <ListItemIcon>
                  <Checkbox
                    edge="start"
                    checked={adoptedSlides[index]}
                    tabIndex={-1}
                    disableRipple
                  />
                </ListItemIcon>
                <ListItemText
                  primary={lang === 'en' ? `Slide ${index + 1}` : `幻灯片 ${index + 1}`}
                  secondary={<ReactMarkdown>{slide.contentDescription}</ReactMarkdown>}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsOutlineAdviserDialogOpen(false)}>{lang === 'en' ? 'Cancel' : '取消'}</Button>
          {outlineFineTuning && (
            <Button
              onClick={() => {
                setWizardMode('generated');
                setIsEditWizardOpen(true);
              }}
              disabled={loading || (generatedOutline.length === 0 && suggestedOutline.length === 0)}
            >
              {lang === 'en' ? 'Fine-tune' : '微调'}
            </Button>
          )}
          <Button variant="contained" onClick={handleConfirmOutline} color="primary" autoFocus>
            {lang === 'en' ? 'Confirm Selection' : '确认选择'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit outline wizard dialog */}
      <EditOutlineWizardDialog
        open={isEditWizardOpen}
        onClose={handleCloseEditWizard}
        lang={lang}
        projectId={project._id}
        outlineItems={wizardMode === 'existing' ? project.slides : (generatedOutline.length > 0 ? generatedOutline : suggestedOutline)}
        onApplyOutline={(updatedSlides) => {
          console.log('updatedSlides', updatedSlides);
          if (wizardMode === 'existing') {
            onUpdateProject({ ...project, slides: updatedSlides });
          } else {
            setGeneratedOutline(updatedSlides);
            setAdoptedSlides(new Array(updatedSlides.length).fill(true));
          }
        }}
        handleSuggestOutlineEdits={handleSuggestOutlineEdits}
      />
    </>
  );
}

export default OutlineManager;
